import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  jobsApi,
  type AtsJob,
  type AtsJobStage,
  type JobsListParams,
  type JobsListResponse,
} from './jobs.api';
import { jobsKeys } from './jobs.keys';
import { isNonEmptyId } from '@/utils/nullish';

/**
 * A list fetch with explicit params — job pickers, description templates, and
 * the jobs page's own `useJobsList`, which derives its params from view state.
 */
export const useJobsListWithParams = (
  params: JobsListParams,
  options?: { enabled?: boolean }
) =>
  useQuery<JobsListResponse>({
    queryKey: jobsKeys.list(params),
    queryFn: () => jobsApi.list(params),
    enabled: options?.enabled ?? true,
    // Keeps the previous page on screen while the next one loads instead of
    // flashing the empty state — the Redux version held `data` through the
    // pending state for the same reason.
    placeholderData: (previous) => previous,
    // Rows this fresh are reused on a remount instead of refetched. Without it
    // every mount refetches (stale immediately), which is what let the page's
    // skeleton (then keyed on `isFetching`) — it unmounts the list it is standing in for — drive
    // a fetch/unmount/fetch loop. The read-only subscribers in JobsCard and
    // JobsTable are the fix; this stops the next fetch-on-mount subscriber
    // restarting it.
    //
    // Thirty seconds, not longer: `refetchOnWindowFocus` is off app-wide and
    // nothing polls, so a remount is the only way another recruiter's new job
    // reaches this list. A user's own creates and archives are unaffected either
    // way — those invalidate the key outright.
    staleTime: 30_000,
  });

export const useJob = (
  jobId?: string | number,
  options?: { enabled?: boolean }
) =>
  useQuery<AtsJob>({
    queryKey: jobsKeys.detail(jobId ?? ''),
    queryFn: () => jobsApi.get({ jobId: jobId as string | number }),
    enabled: (options?.enabled ?? true) && isNonEmptyId(jobId),
    staleTime: 0,
  });

export const useJobStages = (
  jobId?: string | number,
  options?: { enabled?: boolean }
) =>
  useQuery<AtsJobStage[]>({
    queryKey: jobsKeys.stages(jobId ?? ''),
    queryFn: () => jobsApi.stages({ jobId: jobId as string | number }),
    enabled: (options?.enabled ?? true) && isNonEmptyId(jobId),
    staleTime: 0,
  });

/**
 * Every job mutation invalidates the list, which is what the RTK tag did. The
 * hooks are separate rather than one generic mutation so each call site keeps
 * its own `isPending`.
 */
const useJobMutation = <TArgs>(
  mutationFn: (_args: TArgs) => Promise<AtsJob>
) => {
  const queryClient = useQueryClient();

  return useMutation<AtsJob, Error, TArgs>({
    mutationFn,
    onSuccess: () => {
      // Detail/stages only go stale: refetching them would hit the open modal mid-submit.
      queryClient.invalidateQueries({
        queryKey: jobsKeys.all,
        refetchType: 'none',
      });
      queryClient.invalidateQueries({ queryKey: jobsKeys.lists() });
    },
  });
};

export const useCreateJob = () => useJobMutation(jobsApi.create);
export const useUpdateJob = () => useJobMutation(jobsApi.update);
export const useCloneJob = () => useJobMutation(jobsApi.clone);
export const useArchiveJob = () => useJobMutation(jobsApi.archive);
export const useCloseJob = () => useJobMutation(jobsApi.close);
export const useReopenJob = () => useJobMutation(jobsApi.reopen);
